/**
 * FormulaCell Component
 *
 * Renders a read-only formula result with error display.
 * Formula columns are computed server-side and cannot be edited in the cell.
 */

import { ExclamationTriangleIcon, CalculatorIcon } from '@heroicons/react/24/outline';
import type { FormulaCellProps } from './types';

export function FormulaCell({
  value,
  outputType = 'text',
}: FormulaCellProps) {
  // Handle null/undefined value
  if (!value) {
    return (
      <div className="w-full h-full flex items-center">
        <span className="text-gray-400 text-sm">-</span>
      </div>
    );
  }

  // Handle error state
  if (value.error) {
    return (
      <div className="w-full h-full flex items-center">
        <div className="flex items-center gap-1 text-red-500" title={value.error}>
          <ExclamationTriangleIcon className="h-4 w-4" />
          <span className="text-xs font-medium">{value.error}</span>
        </div>
      </div>
    );
  }

  // Handle null result
  if (value.result === null || value.result === undefined) {
    return (
      <div className="w-full h-full flex items-center">
        <span className="text-gray-400 text-sm">-</span>
      </div>
    );
  }

  // Format result based on output type
  const formatResult = () => {
    const result = value.result;

    switch (outputType) {
      case 'number':
        if (typeof result === 'number') {
          return result.toLocaleString();
        }
        return String(result);

      case 'date':
        if (typeof result === 'string') {
          try {
            return new Date(result).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            });
          } catch {
            return result;
          }
        }
        return String(result);

      case 'boolean':
        if (typeof result === 'boolean') {
          return (
            <span
              className={`px-2 py-0.5 text-xs font-medium rounded ${
                result
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {result ? 'Yes' : 'No'}
            </span>
          );
        }
        return String(result);

      case 'text':
      default:
        return String(result);
    }
  };

  const formattedResult = formatResult();

  return (
    <div className="w-full h-full flex items-center" title={`Formula: ${value.formula}`}>
      <div className="flex items-center gap-1.5 min-w-0">
        <CalculatorIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
        {typeof formattedResult === 'string' ? (
          <span className="text-sm text-gray-700 truncate">{formattedResult}</span>
        ) : (
          formattedResult
        )}
      </div>
    </div>
  );
}
